import {
	ButtonBase,
	Icon,
	RewriteToolsMenu,
	SelectionAnchor
} from "client/Components";
import { RichTextEditorRewriteToolHandlers } from "client/Components/RichTextEditor/tools/useRichTextEditorRewriteTools";
import {
	clamp,
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { Rect } from "client/Misc/commonTypes";
import {
	ReactElement,
	Ref,
	useRef,
	useState
} from "react";
import styles from "./RewriteTool.module.less";
import { useResizeObserver } from "client/Hooks";
import { useAiAssistantBranding } from "client/Features/ChatAssistant/useAiAssistantBranding";
import { AiAssistantAvatar } from "client/Features/ChatAssistant/AiAssistantAvatar";
import classNames from "classnames";

export type RewriteToolProps = {
	anchor: SelectionAnchor;
	rewriteHandlers: RichTextEditorRewriteToolHandlers;
	containmentRect?: Rect;
};

export const RewriteTool = (props: RewriteToolProps): ReactElement => {
	const {
		anchor,
		rewriteHandlers,
		containmentRect
	} = props;

	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const buttonRef: Ref<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const containerRef: Ref<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		width: buttonWidth,
		height: buttonHeight
	} = useResizeObserver(containerRef);

	const {
		smallAvatarUrl
	} = useAiAssistantBranding();

	let buttonTop: number = anchor.direction === "forward"
		? anchor.bottom + 8
		: anchor.top - buttonHeight - 8;

	if (isNotNil(containmentRect)) {
		buttonTop = clamp(
			buttonTop,
			containmentRect.top,
			containmentRect.bottom - buttonHeight
		);
	}

	// Anchor to the end of the selection: for a left-to-right ("forward") selection the
	// button grows to the right of the end point; for a right-to-left ("backward") selection
	// it grows to the left of it.
	let buttonLeft: number = anchor.direction === "forward"
		? anchor.x
		: anchor.x - buttonWidth;

	if (containmentRect) {
		buttonLeft = clamp(
			buttonLeft,
			0,
			containmentRect.right - buttonWidth
		);
	}

	return (
		<>
			<div
				ref={containerRef}
				className={
					classNames(
						styles.root,
						{[styles.root_measurements]: buttonWidth === 0}
					)
				}
				style={{
					left: buttonLeft,
					top: buttonTop
				}}
			>
				<ButtonBase
					ref={buttonRef}
					onClick={() => setIsMenuOpen(true)}
					trackingId="improve-text"
					className={styles.button}
				>
					<AiAssistantAvatar
						imageUrl={smallAvatarUrl}
						size="x-small"
						trackingId="avatar"
					/>
					<span>
						{localize("_ImproveText")}
					</span>
					<Icon value="chevron-down" />
				</ButtonBase>
			</div>
			{isMenuOpen && (
				<RewriteToolsMenu
					handlers={rewriteHandlers}
					onClose={() => setIsMenuOpen(false)}
					target={ensureNotNil(buttonRef.current)}
					trackingId="improve-text__drop-over"
					contentAlignment="tl"
					targetAlignment="bl"
				/>
			)}
		</>
	);
};
RewriteTool.displayName = "RewriteTool";